﻿import {
  BellOff,
  Clock3,
  FileText,
  Lock,
  MessageSquare,
  Phone,
  Search,
  Shield,
  Star,
  StickyNote,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Conversation, TimelineItem } from "../data/types";
import { ConversationAvatar } from "./ConversationAvatar";
import { createNote } from "@/services/api/notes";
import { queryKeys } from "@/constants/queryKeys";

type ContactInfoDrawerProps = {
  conversation: Conversation;
  dialNumber?: string;
  timelineItems?: TimelineItem[];
  onClose: () => void;
  onCall?: () => void;
  onSearch?: () => void;
};

export function ContactInfoDrawer({
  conversation,
  dialNumber = "",
  timelineItems = [],
  onClose,
  onCall,
  onSearch,
}: ContactInfoDrawerProps) {
  const queryClient = useQueryClient();
  const [noteDraft, setNoteDraft] = useState("");

  const media = timelineItems.filter(
    (item) =>
      item.kind === "message" &&
      (item.message.msg_type === "image" ||
        item.message.msg_type === "file" ||
        item.message.attachment_url),
  );

  const notes = timelineItems.filter(
    (item): item is Extract<TimelineItem, { kind: "note" }> => item.kind === "note",
  );

  const noteMutation = useMutation({
    mutationFn: () => createNote({ content: noteDraft.trim(), number: dialNumber }),
    onSuccess: () => {
      setNoteDraft("");
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatTimeline(conversation.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notesByNumber(dialNumber.replace(/\D/g, "") || dialNumber),
      });
    },
  });

  return (
    <aside className="absolute inset-0 z-30 flex h-full w-full max-w-full flex-col border-l border-border bg-surface lg:static lg:w-[340px] lg:max-w-[340px] lg:shrink-0">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Close contact info"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-fg-muted hover:bg-surface-muted"
          >
            <X className="h-4 w-4" />
          </button>
          <h3 className="text-sm font-semibold text-fg">
            {conversation.type === "group" ? "Group info" : "Contact info"}
          </h3>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col items-center px-5 py-6">
          <ConversationAvatar conversation={conversation} size="lg" className="scale-[1.35]" />
          <h4 className="mt-5 text-xl font-semibold text-fg">{conversation.name}</h4>
          <p className="mt-1 text-sm text-fg-muted">
            {dialNumber ||
              (conversation.type === "group"
                ? `${conversation.members?.length || 0} members`
                : "No extension")}
          </p>
          <p className="mt-1 text-[11px] uppercase tracking-wide text-fg-subtle">
            {conversation.kind} · {conversation.channel}
          </p>
        </div>

        <div className="mb-4 flex items-center justify-center gap-6 px-4">
          {dialNumber ? (
            <QuickAction label="Voice" icon={<Phone className="h-4 w-4" />} onClick={onCall} />
          ) : null}
          <QuickAction label="Search" icon={<Search className="h-4 w-4" />} onClick={onSearch} />
        </div>

        {dialNumber ? (
          <div className="mx-3 mb-3 overflow-hidden rounded-[10px] border border-amber-500/25 bg-gradient-to-br from-amber-50 to-orange-50/80 dark:border-amber-400/20 dark:from-amber-950/40 dark:to-orange-950/30">
            <div className="border-b border-amber-500/15 px-4 py-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-amber-800 uppercase dark:text-amber-200">
                <StickyNote className="h-3.5 w-3.5" />
                Call notes
              </p>
            </div>
            {notes.length > 0 ? (
              <ul className="max-h-40 space-y-2 overflow-y-auto px-4 py-3">
                {notes.slice(0, 8).map((item) => (
                  <li
                    key={item.id}
                    className="rounded-[8px] border border-amber-500/15 bg-white/60 px-3 py-2 dark:bg-black/20"
                  >
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-fg">
                      {item.note.content}
                    </p>
                    <p className="mt-1 text-[10px] text-amber-900/55 dark:text-amber-100/50">
                      {item.note.author_name ? `${item.note.author_name} · ` : ""}
                      {item.occurred_at
                        ? new Date(item.occurred_at).toLocaleString([], {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </p>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="px-4 py-3">
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={3}
                placeholder="Write a note for this number…"
                className="w-full resize-none rounded-[8px] border border-amber-500/20 bg-white/70 px-3 py-2 text-sm outline-none dark:bg-black/20"
              />
              <button
                type="button"
                disabled={!noteDraft.trim() || noteMutation.isPending}
                onClick={() => noteMutation.mutate()}
                className="mt-2 h-9 w-full rounded-[6px] bg-papaya-leaf text-sm font-medium text-white disabled:opacity-50"
              >
                Save note
              </button>
            </div>
          </div>
        ) : null}

        <div className="mx-3 mb-3 rounded-[6px] border border-border px-4 py-3">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-medium text-fg">Media & docs</p>
            <span className="text-xs text-fg-subtle">{media.length}</span>
          </div>
          {media.length === 0 ? (
            <p className="text-xs text-fg-muted">No shared files yet.</p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {media.slice(0, 12).map((item) => {
                if (item.kind !== "message") return null;
                const msg = item.message;
                if (msg.msg_type === "image" && msg.attachment_url) {
                  return (
                    <a
                      key={item.id}
                      href={msg.attachment_url}
                      target="_blank"
                      rel="noreferrer"
                      className="aspect-square overflow-hidden rounded-[6px] bg-surface-muted"
                    >
                      <img
                        src={msg.attachment_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </a>
                  );
                }
                return (
                  <a
                    key={item.id}
                    href={msg.attachment_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex aspect-square items-center justify-center rounded-[6px] bg-surface-muted text-fg-muted"
                  >
                    <FileText className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {conversation.type === "group" && conversation.members?.length ? (
          <div className="mx-3 mb-3 rounded-[6px] border border-border px-4 py-3">
            <p className="mb-2 text-sm font-medium text-fg">Members</p>
            <ul className="space-y-2">
              {conversation.members.map((member) => (
                <li key={member.id} className="flex items-center gap-2 text-sm">
                  <ConversationAvatar
                    conversation={{
                      name: member.user.name,
                      initials: member.user.initials,
                      tone: "mint",
                      online: member.user.online || member.user.is_online,
                    }}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {member.user.name || member.user.username}
                  </span>
                  <span className="text-[10px] uppercase text-fg-subtle">{member.role}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mx-3 mb-4 overflow-hidden rounded-[6px] border border-border">
          <OptionRow icon={<Star className="h-4 w-4" />} title="Starred messages" />
          <OptionRow
            icon={<BellOff className="h-4 w-4" />}
            title="Mute notifications"
          />
          <OptionRow icon={<Clock3 className="h-4 w-4" />} title="Disappearing messages" />
          <OptionRow icon={<Lock className="h-4 w-4" />} title="Encryption" />
          <OptionRow icon={<Shield className="h-4 w-4" />} title="Privacy" />
          <OptionRow icon={<MessageSquare className="h-4 w-4" />} title="Clear chat" />
        </div>
      </div>
    </aside>
  );
}

function QuickAction({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-1.5">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border text-fg-muted hover:bg-surface-muted">
        {icon}
      </span>
      <span className="text-xs text-fg-muted">{label}</span>
    </button>
  );
}

function OptionRow({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
      <span className="text-fg-muted">{icon}</span>
      <span className="text-sm text-fg">{title}</span>
    </div>
  );
}
